<script setup>

import SubmitButtonLargeComponent from '@buttons/SubmitButtonLargeComponent.vue';
import {computed, inject, ref, watch} from 'vue';
import {debounce} from 'lodash';
import {showErrorToast, showSuccessToast} from '@utils/Toast';
import {ArrowLeftIcon} from '@heroicons/vue/24/solid';
import ButtonComponent from '@buttons/ButtonComponent.vue';
import MainProgressBarComponent from '../../Helper/MainProgressBarComponent.vue';
import {handleErrorResponse} from '@utils/errorHandler';

const props = defineProps({
    csrf: String,
    route: String,
    routeSuccess: String,
    user: Object,
});

// Inject Axios
const axios = inject('axios');

const passwordRegex = /^(?=\S*$)(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*\-]).{10,20}$/;

const title = 'Welcome to your EVED Portal<br />Please take a moment to set up your password.';
const passwordInfo =
    '6 characters minimum length.<br />(At least 1 lower case + 1 upper case + 1 number + 1 special character)';

//Handle with form
const password = ref('');
const passwordAgain = ref('');

const errors = ref({
    password: null,
    passwordAgain: null,
    bothPasswordMatch: null,
});

const uploadPercentage = computed(() => (1/6) * 100 );

const userPassword = ref(props.user.password);

const isFormValid = ref(false);

const delayedRedirection = debounce(() => {
    window.location.href = props.routeSuccess;
}, 2000);

const validateForm = () => {
    // Check the validity of the input fields
    const isPasswordValid = password.value !== '' && passwordRegex.test(password.value);
    const isPasswordAgainValid = passwordAgain.value !== '' && passwordRegex.test(passwordAgain.value);
    const bothPasswordMatch = (isPasswordValid && isPasswordAgainValid && password.value === passwordAgain.value) ||
        password.value === '' || passwordAgain.value === '';

    errors.value.password = !isPasswordValid ? 'Invalid password format' : null;
    errors.value.passwordAgain = !isPasswordAgainValid ? 'Invalid password format' : null;
    errors.value.bothPasswordMatch = !bothPasswordMatch ? 'Passwords do not match' : null;

    isFormValid.value = isPasswordValid && isPasswordAgainValid && bothPasswordMatch;
    };

const submitForm = async () => {
    validateForm();
    if(!isFormValid.value) {
        // showErrorToast('Invalid form values');
        return false;
    }
    //Prepare the data to be sent
    const formData = {
        password: password.value,
        password_confirmation: passwordAgain.value,
    };

    try {
        //Send a POST request to the server
        const response = await axios.post(props.route, formData, {
            headers: {
                'X-CSRF-TOKEN': props.csrf // Include CSRF token here
            }
        });
        //Handle response here
        if(response.status === 200) {
            const data = response.data;
            if(data.success === 1) {
                showSuccessToast(data.message);
                delayedRedirection();
            } else {
                showErrorToast(data.message);
            }
        } else {
            if(response.message) {
                showErrorToast(response.message);
            }
        }

    } catch (error) {
        showErrorToast(handleErrorResponse(error));
        console.error('Submission error', error);
    }
};

</script>

<template>
    <div class="container sm:max-w-6xl text-center py-6">
        <main-progress-bar-component :upload-percentage="uploadPercentage"></main-progress-bar-component>
        <div class="font-bold text-xl md:text-2xl pb-6 pt-12 md:pt-20 leading-tight" v-html="title"></div>

        <form :action="route" id="frmClientLogin" method="post"
              @submit.prevent="submitForm"
        >
            <div class="text-center container max-w-100 md:w-100 bg-white rounded-xl mb-8">
                <div class="wrap-form">
                    <div>
                        <div class="form-row">
                            <input type="password"
                                   v-model="password"
                                   class="form-control z-10"
                                   placeholder="Password"
                                   autocomplete="off"
                            >
                            <div v-if="errors.password" class="py-2 text-red text-left" v-text="errors.password"></div>
                        </div>
                        <div class="form-row !mb-0">
                            <input type="password"
                                   v-model="passwordAgain"
                                   class="form-control z-10"
                                   placeholder="Re-Type Password"
                                   autocomplete="off"
                            >
                            <div v-if="errors.passwordAgain" class="py-2 text-red text-left"
                                 v-text="errors.passwordAgain"></div>
                            <div v-if="errors.bothPasswordMatch" class="py-2 text-red text-left"
                                 v-text="errors.bothPasswordMatch"></div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="py-6 text-primary-1/75 text-center text-sm" v-html="passwordInfo"></div>
            <div class="container max-w-100 md:w-100">
                <SubmitButtonLargeComponent
                    title="Continue"
                    btn-class="btn-green-5 w-full !py-4"
                >
                </SubmitButtonLargeComponent>
            </div>
        </form>
    </div>
</template>

<style scoped lang="scss">
.form {
    &-row {
        @apply mb-6;
    }
    &-control {
        @apply bg-white py-3;
    }
}
</style>
